'use client';

import Input from '@/components/ui/Input';
import Select from '@/components/ui/Select';
import { useUserStore } from '@/store/useUserStore';
import { countries } from '@/utils/countries';
import { handleFormSubmitApiError } from '@/utils/form-submit-error';
import { createClient } from '@/utils/supabase/client';
import { cn } from '@/utils/tailwind-merge';
import { personalInfoSchema } from '@/utils/validation-schema';
import { zodResolver } from '@hookform/resolvers/zod';
import { useTranslations } from 'next-intl';
import { useEffect, useState } from 'react';
import { Controller, useForm } from 'react-hook-form';
import { z } from 'zod';
import Button from '../ui/Button';

type FormData = z.infer<typeof personalInfoSchema>;

export default function PersonalInfoForm({ className }: { className?: string }) {
    const t = useTranslations();
    const [submitting, setSubmitting] = useState<boolean>(false);
    const [isSuccessful, setIsSuccessful] = useState<boolean>(false);
    const { user } = useUserStore();
    const supabase = createClient();
    const [userData, setUserData] = useState<any | null>(null);

    const {
        handleSubmit,
        register,
        control,
        reset,
        formState: { errors },
    } = useForm<FormData>({
        resolver: zodResolver(personalInfoSchema),
        defaultValues: {
            firstName: userData?.firstName || '',
            lastName: userData?.lastName || '',
            pronouns: userData?.pronouns || '',
            email: userData?.email || '',
            phone: userData?.phone || '',
            companyName: userData?.companyName || '',
            streetAddress: userData?.streetAddress || '',
            city: userData?.city || '',
            province: userData?.province || '',
            postalCode: userData?.postalCode || '',
            country: userData?.country || 'CA',
        },
    });

    const fetchUser = async () => {
        if (!user) {
            return;
        }

        const { data, error } = await supabase
            .from('profiles')
            .select('*')
            .eq('user_id', user.id)
            .single();

        if (error) {
            console.error('Error fetching user data', error);
            return;
        }

        setUserData({
            firstName: data?.first_name,
            lastName: data?.last_name,
            pronouns: data?.pronouns,
            email: user.email,
            phone: data?.phone_number,
            companyName: data?.company_name,
            streetAddress: data?.street_address,
            city: data?.city,
            province: data?.province,
            postalCode: data?.postal_code,
            country: data?.country,
        });
        reset({
            firstName: data?.first_name || '',
            lastName: data?.last_name || '',
            pronouns: data?.pronouns || '',
            email: user.email || '',
            phone: data?.phone_number || '',
            companyName: data?.company_name || '',
            streetAddress: data?.street_address || '',
            city: data?.city || '',
            province: data?.province || '',
            postalCode: data?.postal_code || '',
            country: data?.country || '',
        });
    };

    const onSubmit = async (formData: FormData) => {
        setSubmitting(true);
        setIsSuccessful(false);

        if (!user) {
            setSubmitting(false);
            return;
        }
        try {
            const { error } = await supabase
                .from('profiles')
                .update({
                    first_name: formData.firstName,
                    last_name: formData.lastName,
                    pronouns: formData.pronouns || null,
                    phone_number: formData.phone?.trim() || null,
                    company_name: formData.companyName?.trim() || null,
                    street_address: formData.streetAddress?.trim() || null,
                    city: formData.city?.trim() || null,
                    province: formData.province?.trim() || null,
                    postal_code: formData.postalCode?.trim() || null,
                    country: formData.country || null,
                })
                .eq('user_id', user.id);

            if (error) {
                throw new Error('Failed to submit the form');
            }

            setIsSuccessful(true);
            await fetchUser();
        } catch (error) {
            handleFormSubmitApiError(error, t);
        } finally {
            setSubmitting(false);
        }
    };

    const getFieldError = (fieldName: keyof FormData) =>
        errors[fieldName]?.message && errors[fieldName]?.message !== 'Required'
            ? t(`FieldErrors.${errors[fieldName]?.message}`)
            : '';

    useEffect(() => {
        fetchUser();
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    return (
        <form
            className={cn('flex flex-col lg:gap-12 gap-7 relative', className)}
            onSubmit={handleSubmit(onSubmit)}
            noValidate
        >
            <div className="grid grid-cols-1 xl:grid-cols-3 lg:gap-5 gap-3 w-full mt-5 lg:mt-12">
                <Input
                    {...register('firstName')}
                    className="newsletter-input"
                    type="text"
                    name="firstName"
                    id="firstName"
                    placeholder={t('Form.firstName')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('firstName')}
                    defaultValue={userData?.firstName}
                />
                <Input
                    {...register('lastName')}
                    className="newsletter-input"
                    type="text"
                    name="lastName"
                    id="lastName"
                    placeholder={t('Form.lastName')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('lastName')}
                    defaultValue={userData?.lastName}
                />
                <Input
                    {...register('pronouns')}
                    className="newsletter-input"
                    type="text"
                    name="pronouns"
                    id="pronouns"
                    placeholder={t('Form.pronouns')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('pronouns')}
                    defaultValue={userData?.pronouns}
                />
                <Input
                    {...register('email')}
                    className="newsletter-input"
                    type="email"
                    name="email"
                    id="email"
                    placeholder={t('Form.email')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('email')}
                    defaultValue={userData?.email}
                    disabled
                    readOnly
                />
                <Input
                    {...register('phone')}
                    className="newsletter-input"
                    type="text"
                    name="phone"
                    id="phone"
                    placeholder={t('Form.phone')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('phone')}
                    defaultValue={userData?.phone}
                />
                <Input
                    {...register('companyName')}
                    className="newsletter-input"
                    type="text"
                    name="companyName"
                    id="companyName"
                    placeholder={t('Form.companyName')}
                    autoComplete="false"
                    color="white"
                    error={getFieldError('companyName')}
                    defaultValue={userData?.companyName}
                />
            </div>

            <div className="flex flex-col gap-5">
                <h4 className="text-white text-[32px] lg:text-[46px] uppercase">
                    {t('Account.contactAddress')}
                </h4>
                <div className="grid grid-cols-1 xl:grid-cols-3 lg:gap-5 gap-3 w-full">
                    <Input
                        {...register('streetAddress')}
                        className="newsletter-input"
                        type="text"
                        name="streetAddress"
                        id="streetAddress"
                        placeholder={t('Form.streetAddress')}
                        autoComplete="false"
                        color="white"
                        error={getFieldError('streetAddress')}
                        defaultValue={userData?.streetAddress}
                    />
                    <Input
                        {...register('city')}
                        className="newsletter-input"
                        type="text"
                        name="city"
                        id="city"
                        placeholder={t('Form.city')}
                        autoComplete="false"
                        color="white"
                        error={getFieldError('city')}
                        defaultValue={userData?.city}
                    />
                    <Input
                        {...register('province')}
                        className="newsletter-input"
                        type="text"
                        name="province"
                        id="province"
                        placeholder={t('Form.province')}
                        autoComplete="false"
                        color="white"
                        error={getFieldError('province')}
                        defaultValue={userData?.province}
                    />
                    <Input
                        {...register('postalCode')}
                        className="newsletter-input"
                        type="text"
                        name="postalCode"
                        id="postalCode"
                        placeholder={t('Form.postalCode')}
                        autoComplete="false"
                        color="white"
                        error={getFieldError('postalCode')}
                        defaultValue={userData?.postalCode}
                    />
                    <Controller
                        name="country"
                        control={control}
                        defaultValue={userData?.country || 'CA'}
                        render={({ field }) => (
                            <Select
                                {...field}
                                value={field.value}
                                onChange={(e) => field.onChange(e.target.value)}
                                options={countries}
                                placeholder={t('Form.country')}
                            />
                        )}
                    />
                </div>
            </div>

            {isSuccessful && (
                <div className="text-green-500 text-sm mt-2">{t('Form.successSubtitle')}</div>
            )}
            <Button
                className="w-full lg:w-fit"
                type="submit"
                variant="secondary"
                title={t('Form.save')}
                submitting={submitting}
                disabled={submitting}
            />
        </form>
    );
}
